<template>
  <div id="app" class="flex flex-col min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
    <header class="flex flex-col items-center justify-center mt-8 mb-12">
      <img src="/src/assets/logo-bg.png" alt="Trivia Generator Logo" class="h-24 sm:h-32 mb-6 mx-4 transform hover:scale-105 transition-transform duration-300" />
      <h2 class="md:text-2xl text-lg text-center mt-4 mb-2 px-4 text-gray-800">
        Generate trivia questions on <span class="font-bold text-purple-600">any</span> topic
      </h2>
      <p class="text-gray-600 text-center px-4">Powered by AI, and completely free!</p>
    </header>

    <div class="flex-grow flex flex-row items-center justify-center relative">
      <main class="flex-grow flex flex-col items-center w-full lg:w-4/6 mx-4">
        <h1 class="md:text-4xl text-2xl text-center mt-4 mb-8 font-bold text-gray-800">
          Which topic would you like questions on?
        </h1>
        <form @submit.prevent="fetchTrivia" class="mb-4 w-full max-w-xl items-center">
          <div class="mb-6 w-full max-w-xl">
            <input 
              v-model="topic" 
              type="text" 
              :placeholder="currentExampleTopic" 
              class="w-full p-4 border-2 border-gray-200 rounded-xl text-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 shadow-sm hover:shadow-md placeholder-transition" 
            />
          </div>
          <div class="mb-8 w-full max-w-xl">
            <input 
              id="difficulty" 
              type="range" 
              v-model="difficulty" 
              min="0" 
              max="3" 
              step="1"
              class="w-full mt-2 accent-purple-600 hover:accent-purple-700 rounded-lg" 
            />
            <label for="difficulty" class="block text-lg font-medium text-gray-700 mt-2">
              Difficulty: <span class="text-purple-600">{{ difficultyLevels[difficulty] }}</span>
            </label>
          </div>
          <button 
            v-if="!loading" 
            type="submit"
            class="bg-purple-600 text-white px-8 py-4 rounded-xl text-xl w-full hover:bg-purple-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Generate
          </button>
          <button 
            v-if="loading" 
            type="button" 
            class="bg-purple-600 text-white px-8 py-4 rounded-xl text-xl w-full flex items-center justify-center space-x-3 shadow-md"
            disabled
          >
            <span class="spinner-border inline-block" role="status" aria-hidden="true"></span>
            <span>{{ streamInProgress ? `Generating questions (${questions.length}/20)...` : 'Thinking...' }}</span>
          </button>
        </form>
        <div v-if="error" class="mt-8 p-4 rounded-lg border-l-4" 
             :class="isInappropriateContentError ? 'bg-orange-50 border-orange-400 text-orange-800' : 'bg-red-50 border-red-400 text-red-600'">
          <div class="flex items-start">
            <div class="flex-shrink-0 mr-3">
              <svg v-if="isInappropriateContentError" class="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <svg v-else class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 v-if="isInappropriateContentError" class="text-sm font-medium text-orange-800">
                Content Not Allowed
              </h3>
              <h3 v-else class="text-sm font-medium text-red-800">
                Error
              </h3>
              <div class="mt-1 text-sm" :class="isInappropriateContentError ? 'text-orange-700' : 'text-red-600'">
                {{ error }}
              </div>
              <div v-if="isInappropriateContentError" class="mt-3 text-sm text-orange-600">
                <p class="font-medium">Try these topics instead:</p>
                <div class="mt-1 flex flex-wrap gap-2">
                  <button 
                    v-for="suggestion in topicSuggestions" 
                    :key="suggestion"
                    @click="selectSuggestedTopic(suggestion)"
                    class="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs hover:bg-orange-200 transition-colors duration-200"
                  >
                    {{ suggestion }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <transition-group 
          tag="div" 
          name="fade" 
          class="mt-8 w-full max-w-xl" 
          v-if="questions.length > 0"
        >
          <div key="container" class="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
            <transition-group tag="ul" name="list" class="divide-y divide-gray-100">
              <li v-for="(item, index) in questions" :key="index"
                class="py-6 first:pt-0 last:pb-0 question-item">
                <p class="font-semibold text-lg text-gray-800 mb-2">Q{{ index + 1 }}: {{ item.question }}</p>
                <p class="text-gray-600">A: {{ item.answer }}</p>
              </li>
            </transition-group>
          </div>
        </transition-group>
      </main>
    </div>
    <footer class="w-full py-6 text-center text-gray-600 bg-white border-t border-gray-100">
      <div class="max-w-xl mx-auto px-4">
        <p class="text-sm text-gray-500 mt-2">
          Feedback? <a href="mailto:feedback@triviagenerator.com" class="text-purple-600 hover:text-purple-700">feedback@triviagenerator.com</a>
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  data() {
    return {
      topic: "",
      difficulty: 1,
      difficultyLevels: ["Easy", "Medium", "Hard", "Impossible"],
      questions: [],
      loading: false,
      streamInProgress: false,
      error: "",
      isMobile: false,
      currentExampleTopic: "",
      exampleTopicIndex: 0,
      exampleTopicInterval: null,
      exampleTopics: [
        "Australian history",
        "Aircraft",
        "COVID-19",
        "Disney",
        "German Shepherds",
        "Geography",
        "NBA",
        "Taylor Swift",
        "The human body",
        "The periodic table",
        "The solar system",
        "The Titanic",
        "The Vietnam War",
        "The World Cup",
        "The Wright brothers",
        "The Office",
        "The Zodiac Killer",
        "Theatre",
        "Thomas Edison",
        "Trains",
        "Twilight",
        "The Simpsons",
        "Oceans",
        "World War 2",
        "Goldfish",
        "The Wiggles",
        "Airports",
        "Dinosaurs",
        "New Zealand birds",
        "Pokemon",
        "General knowledge",
        "History",
        "Ancient Egypt",
        "Artificial Intelligence",
        "Baseball",
        "Beethoven",
        "Black holes",
        "Chocolate",
        "Coffee",
        "Comic books",
        "Cryptocurrency",
        "Dance",
        "Egyptian pyramids",
        "Evolution",
        "Famous inventors",
        "Greek mythology",
        "Harry Potter",
        "Ice cream",
        "Jazz music",
        "Leonardo da Vinci",
        "Mars",
        "Medieval knights",
        "Minecraft",
        "Mount Everest",
        "Mozart",
        "Nintendo",
        "Olympic Games",
        "Pizza",
        "Quantum physics",
        "Rainforests",
        "Robots",
        "Rock music",
        "Sharks",
        "Space exploration",
        "Star Wars",
        "Superheroes",
        "Sushi",
        "Tennis",
        "The Beatles",
        "The Great Wall of China",
        "The Internet",
        "The Mona Lisa",
        "The Moon landing",
        "The Renaissance",
        "The Roman Empire",
        "The Silk Road",
        "The Vikings",
        "Video games",
        "Volcanoes",
        "Whales",
        "World War 1",
        "Yoga",
        "Zombies"
      ],
      timeoutId: null,
      currentReader: null,
      abortController: null,
      isInappropriateContentError: false,
      topicSuggestions: []
    };
  },
  computed: {
    exampleTopic() {
      return this.exampleTopics[Math.floor(Math.random() * this.exampleTopics.length)];
    },
    difficultyText() {
      return this.difficultyLevels[this.difficulty];
    }
  },
  methods: {
    startExampleTopicCycle() {
      // Clear any existing interval
      if (this.exampleTopicInterval) {
        clearInterval(this.exampleTopicInterval);
      }
      
      // Set initial topic randomly
      this.currentExampleTopic = this.exampleTopics[Math.floor(Math.random() * this.exampleTopics.length)];
      
      // Start cycling through topics randomly
      this.exampleTopicInterval = setInterval(() => {
        // Get a random topic that's different from the current one
        let newTopic;
        do {
          newTopic = this.exampleTopics[Math.floor(Math.random() * this.exampleTopics.length)];
        } while (newTopic === this.currentExampleTopic);
        
        this.currentExampleTopic = newTopic;
      }, 1000); // Change every 0.5 seconds
    },
    
    stopExampleTopicCycle() {
      if (this.exampleTopicInterval) {
        clearInterval(this.exampleTopicInterval);
        this.exampleTopicInterval = null;
      }
    },
    async fetchTrivia() {
      // Don't start a new request if one is in progress
      if (this.loading) return;
      
      this.loading = true;
      this.error = "";
      this.questions = [];
      this.isInappropriateContentError = false; // Reset inappropriate content flag
      
      // Clear any existing timeout
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      
      // Create a new abort controller for this request
      this.abortController = new AbortController();
      
      // Set a timeout to handle hanging requests - longer for impossible difficulty
      this.timeoutId = setTimeout(() => {
        if (this.streamInProgress && this.questions.length === 0) {
          this.error = "Request is taking too long. Please try again.";
          this.isInappropriateContentError = false;
          this.cancelStream();
        }
      }, this.difficulty == 3 ? 45000 : 30000); // Keep longer timeout for impossible difficulty
      
      try {
        // Make a POST request to initiate the stream
        const response = await fetch("https://tltvosmxkjaezisnonra.supabase.co/functions/v1/openai", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            topic: this.topic,
            difficulty: this.difficultyLevels[this.difficulty]
          }),
          signal: this.abortController.signal
        });
        
        // Handle inappropriate content errors specifically
        if (!response.ok) {
          if (response.status === 400) {
            // Try to parse the error response for inappropriate content
            try {
              const errorData = await response.json();
              if (errorData.code === 'INAPPROPRIATE_CONTENT') {
                this.error = errorData.message || "The requested topic contains inappropriate content. Please choose a different topic.";
                this.loading = false;
                if (this.timeoutId) {
                  clearTimeout(this.timeoutId);
                  this.timeoutId = null;
                }
                this.isInappropriateContentError = true;
                this.topicSuggestions = this.generateTopicSuggestions(this.topic);
                return;
              } else {
                // Other 400 errors
                this.error = errorData.message || "Invalid request. Please try a different topic.";
                this.loading = false;
                if (this.timeoutId) {
                  clearTimeout(this.timeoutId);
                  this.timeoutId = null;
                }
                this.isInappropriateContentError = false;
                return;
              }
            } catch (parseError) {
              // If we can't parse the error response, fall back to generic message
              this.error = "Invalid request. Please try a different topic.";
              this.loading = false;
              if (this.timeoutId) {
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
              }
              this.isInappropriateContentError = false;
              return;
            }
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }
        
        if (!response.body) {
          throw new Error("Response has no body stream");
        }
        
        // Set up stream processing
        this.processStream(response.body);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
        } else {
          console.error("Error initiating trivia stream:", error);
          this.error = "Please try again, and rephrase your topic.";
          this.isInappropriateContentError = false;
        }
        this.loading = false;
        
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      }
    },
    
    async processStream(stream) {
      this.currentReader = stream.getReader();
      const decoder = new TextDecoder();
      
      // Mark streaming as started
      this.streamInProgress = true;
      
      try {
        let buffer = '';
        
        while (true) {
          const { done, value } = await this.currentReader.read();
          
          if (done) {
            console.log("Stream complete");
            break;
          }
          
          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          // Find complete SSE messages
          const messages = buffer.split('\n\n');
          // Keep the last partial message in the buffer
          buffer = messages.pop() || '';
          
          // Process complete messages
          for (const message of messages) {
            if (message.startsWith('data: ')) {
              const data = message.substring(6); // Remove 'data: ' prefix
              this.handleEventData(data);
              
              // If we've reached 20 questions, stop processing
              if (this.questions.length === 20) {
                break;
              }
            }
          }
          
          // If we've reached 20 questions, exit the processing loop
          if (this.questions.length === 20 && !this.streamInProgress) {
            break;
          }
        }
        
        // Process any remaining data
        if (this.streamInProgress && buffer && buffer.startsWith('data: ')) {
          const data = buffer.substring(6);
          this.handleEventData(data);
        }
        
        // Streaming is complete
        this.finalizeStream();
      } catch (error) {
        if (error.name === 'AbortError' || error.message.includes('aborted')) {
          console.log("Stream processing aborted");
        } else {
          console.error("Error processing stream:", error);
          this.error = "Error processing questions. Please try again.";
          this.isInappropriateContentError = false;
        }
        
        if (this.streamInProgress) {
          this.streamInProgress = false;
          this.loading = false;
        }
      } finally {
        // Clean up
        if (this.currentReader) {
          try {
            // Release the reader
            this.currentReader.releaseLock();
          } catch (e) {
            // Ignore errors during cleanup
          }
          this.currentReader = null;
        }
        
        // Clear timeout
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
      }
    },
    
    handleEventData(data) {
      try {
        const parsedData = JSON.parse(data);
        console.log("Received event:", parsedData.type);
        
        if (parsedData.type === 'question' && parsedData.data) {
          // Check if we already have 20 questions
          if (this.questions.length >= 20) {
            console.log(`Already have ${this.questions.length} questions, ignoring additional questions`);
            // If we have enough questions, finalize the stream
            if (this.streamInProgress) {
              this.cancelStream();
            }
            return;
          }
          
          // Add the question to the list (using Vue.set to ensure reactivity)
          this.questions.push(parsedData.data);
          
          // If we've just reached our target question count, finalize the stream
          if (this.questions.length === 20) {
            console.log(`Reached ${this.questions.length} questions, finalizing stream`);
            this.cancelStream();
            return;
          }
          
          // Reset timeout each time we get a question
          if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = setTimeout(() => {
              if (this.streamInProgress) {
                console.log("Stream timeout after receiving questions");
                this.finalizeStream();
              }
            }, this.difficulty == 3 ? 30000 : 15000); // Keep longer timeout for impossible difficulty
          }
        } else if (parsedData.type === 'error') {
          // Display better error messages
          let errorMsg = parsedData.message || "An error occurred while generating questions.";
          
          // Check if we have any questions despite the error
          if (this.questions.length > 0) {
            // If we have some questions, show a less alarming message
            this.error = `Got ${this.questions.length} questions. Try again for more questions.`;
            this.isInappropriateContentError = false;
          } else {
            // No questions were generated
            this.error = errorMsg;
            this.isInappropriateContentError = false;
          }
          
          this.finalizeStream();
        } else if (parsedData.type === 'end') {
          console.log(`Stream ended with ${parsedData.count || 0} questions, success: ${parsedData.success || false}`);
          
          // If we got fewer than 20 questions, show a generic message
          if (this.questions.length > 0 && this.questions.length < 20) {
            this.error = `Generated ${this.questions.length} questions. Try again for more questions.`;
            this.isInappropriateContentError = false;
          }
          
          this.finalizeStream();
        } else if (parsedData.type === 'status') {
          // Handle status messages
          console.log(`Status update: ${parsedData.message}`);
          // You could display these to the user if desired
        }
      } catch (e) {
        console.error('Error parsing event data:', e, data);
      }
    },
    
    cancelStream() {
      // Cancel the ongoing request
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      
      // Cancel the current reader if exists
      if (this.currentReader) {
        try {
          this.currentReader.cancel("Reached 20 questions");
        } catch (e) {
          console.error("Error cancelling reader:", e);
        }
      }
      
      // Finalize the stream
      this.finalizeStream();
    },
    
    finalizeStream() {
      this.streamInProgress = false;
      this.loading = false;
      
      // Clear any timeouts
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      
      // Ensure we only keep 20 questions if we have more
      if (this.questions.length > 20) {
        console.log(`Trimming excess questions (${this.questions.length}) to 20`);
        this.questions = this.questions.slice(0, 20);
      }
      
      // Check results
      if (this.questions.length === 0) {
        this.error = "No questions were generated. Please try again with a different topic.";
        this.isInappropriateContentError = false;
      } else if (this.questions.length < 20) {
        console.log(`Only received ${this.questions.length} questions, which is less than expected.`);
        // Show a message if we got fewer than 20 questions
        if (!this.error) {
          this.error = `Generated ${this.questions.length} questions. Try again to get 20 questions.`;
          this.isInappropriateContentError = false;
        }
      }
    },
    
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 768;
    },

    generateTopicSuggestions(topic) {
      // Generate relevant, safe topic suggestions
      const safeTopics = [
        "Science", "History", "Geography", "Movies", "Books", "Music", 
        "Sports", "Animals", "Space", "Technology", "Art", "Nature",
        "Food", "Travel", "Inventions", "Famous People", "Mythology",
        "Ocean Life", "Dinosaurs", "Ancient Civilizations"
      ];
      
      // Get 4 random safe topics
      const shuffled = [...safeTopics].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, 4);
    },

    selectSuggestedTopic(topic) {
      this.topic = topic;
      this.error = "";
      this.isInappropriateContentError = false;
    }
  },
  watch: {
    topic(newValue) {
      if (!newValue) {
        this.startExampleTopicCycle();
      } else {
        this.stopExampleTopicCycle();
      }
    }
  },
  mounted() {
    this.checkIfMobile();
    window.addEventListener("resize", this.checkIfMobile);
    this.startExampleTopicCycle(); // Start cycling when component mounts
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkIfMobile);
    this.stopExampleTopicCycle(); // Clean up interval
    this.cancelStream();
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  },
};
</script>

<style>
/* Transition for list items */
.list-enter-active {
  transition: all 0.5s ease;
}

.list-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

/* Fade transition for container */
.fade-enter-active, 
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Ensure the question item transitions smoothly */
.question-item {
  transition: all 0.3s ease;
}

/* Spinner animation */
.spinner-border {
  display: inline-block;
  width: 1.5rem;
  height: 1.5rem;
  border: 0.2em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* Add hover effect to generate button */
button:not([disabled]):hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Add transition for placeholder text */
.placeholder-transition::placeholder {
  transition: opacity 0.5s ease-in-out;
  opacity: 1;
}

.placeholder-transition:focus::placeholder {
  opacity: 0.7;
}

/* Add fade animation for placeholder text changes */
@keyframes placeholderFade {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.placeholder-transition::placeholder {
  animation: placeholderFade 1.5s ease-in-out infinite;
}
</style>
